import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ResolvePagination from 'components/ResolvePagination';
import { DataRoomReport, ReportFilterType } from './types';
import ReportLog from './components/ReportLog';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import ReportsEmptyState from './components/ReportsEmptyState';
import { SIZE } from 'models/Size';
import ReportsFilters from './components/ReportsFilters';
import { safeParse, safeStringify, sentryError } from 'utils';
import { Loader } from 'ncoded-component-library';
import useQueryParams from 'hooks/useQueryParams';
import ExclamationIcon from 'icons/Exclamation.icon';
import { NAVIGATION_VARIANTS } from 'models/Navigation';
import { FIRST_PAGE } from 'data/global';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import usePageParams from 'hooks/usePageParams';

import './Reports.styles.scss';
import './Reports.styles.responsive.scss';

const Reports: React.FC = () => {
  const [totalPages, setTotalPages] = useState(0);
  const [reports, setReports] = useState<DataRoomReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const page = usePageParams();
  const {
    params: { action, memberRole, dataRoomMemberId, from, to, qaAction },
    setQueryParam,
  } = useQueryParams<ReportFilterType>();

  const { t } = useTranslation();

  const { limit } = useNumberOfResultsPerPage();

  const hasActiveFilters = useMemo(
    () => !!action || !!memberRole || !!dataRoomMemberId || !!from || !!to,
    [action, dataRoomMemberId, from, memberRole, to],
  );

  const shouldRenderEmptyState =
    !isLoading && !hasActiveFilters && !reports.length;

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const parsedAction = safeParse(action) || [];
        const parsedQaAction = safeParse(qaAction) || [];

        const {
          data: { items, totalPages },
        } = await dataRoomApi.reports.getReports(dataRoomId, {
          $page: page,
          $limit: limit,
          action:
            parsedAction.length || parsedQaAction.length
              ? safeStringify([...parsedAction, ...parsedQaAction])
              : null,
          memberRole,
          dataRoomMemberId,
          createdAt: from || to ? { $gte: from, $lte: to } : null,
        });

        if (+page > 1 && +page > totalPages) {
          setQueryParam('page', FIRST_PAGE);
        }

        setReports(items);
        setTotalPages(totalPages);
      } catch (error) {
        sentryError(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    action,
    dataRoomId,
    page,
    memberRole,
    dataRoomMemberId,
    from,
    to,
    qaAction,
    limit,
    setQueryParam,
  ]);

  return (
    <div className="reports-page__container white-panel">
      {shouldRenderEmptyState ? (
        <ReportsEmptyState size={SIZE.BIG} />
      ) : (
        <>
          <ReportsFilters variant={NAVIGATION_VARIANTS.TERTIARY} />
          {isLoading ? (
            <Loader />
          ) : !reports.length ? (
            <p className="no-result-filter">
              <ExclamationIcon />
              {t('noResultsWithReportFilters')}
            </p>
          ) : (
            <>
              <div className="reports-page__container__logs">
                {reports.map((report) => (
                  <ReportLog key={report._id} report={report} />
                ))}
              </div>
              <ResolvePagination totalPages={totalPages} />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;
